<template>
  <div class="home-about-page">
    <!-- Background Image -->
    <div class="about-banner-bg"></div>
    
    <!-- Intro Section -->
    <AboutIntroComp :content="introContent" />

    <!-- Mission & Vision Section -->
    <AboutMissionVisionComp :content="missionVisionContent" />

    <!-- Team Snapshot Section -->
    <AboutTeamComp :content="teamContent" />

    <!-- Our Journey Section -->
    <AboutJourneyComp :content="journeyContent" />

    <!-- Clients & Partners Section -->
    <AboutClientsComp :content="clientsContent" />

    <!-- Call to Action Section -->
    <AboutCtaComp :content="ctaContent" />
  </div>
</template>

<script>
import { onMounted, ref } from 'vue'
import { WOW } from 'wowjs'
import AboutIntroComp from '@/components/AboutIntroComp.vue'
import AboutMissionVisionComp from '@/components/AboutMissionVisionComp.vue'
import AboutTeamComp from '@/components/AboutTeamComp.vue'
import AboutJourneyComp from '@/components/AboutJourneyComp.vue'
import AboutClientsComp from '@/components/AboutClientsComp.vue'
import AboutCtaComp from '@/components/AboutCtaComp.vue'

export default {
  name: 'HomeAboutPage',
  components: {
    AboutIntroComp,
    AboutMissionVisionComp,
    AboutTeamComp,
    AboutJourneyComp,
    AboutClientsComp,
    AboutCtaComp
  },
  setup() {
    const introContent = ref({
      sectionLabel: 'About Sogdo',
      mainTitle: 'Who We Are',
      description: 'We are a forward-thinking technology company dedicated to transforming ideas into innovative solutions. Our team of experts combines deep technical knowledge with creative problem-solving to deliver exceptional results for our clients worldwide.',
      stats: [
        { number: '500+', label: 'Projects Completed' },
        { number: '50+', label: 'Happy Clients' },
        { number: '10+', label: 'Years Experience' },
        { number: '24/7', label: 'Support Available' }
      ],
      image: '/resource/about-us.png',
      features: [
        {
          icon: 'fas fa-lightbulb',
          title: 'Innovation First',
          description: 'We embrace cutting-edge technologies and innovative approaches to solve complex challenges.'
        },
        {
          icon: 'fas fa-users',
          title: 'Client-Centric',
          description: 'Our solutions are tailored to meet the unique needs and goals of each client.'
        },
        {
          icon: 'fas fa-rocket',
          title: 'Fast Delivery',
          description: 'We deliver high-quality solutions on time, every time, without compromising on quality.'
        }
      ]
    })

    const missionVisionContent = ref({
      mission: {
        title: 'Our Mission',
        description: 'To empower businesses and individuals through innovative technology solutions that drive growth, efficiency, and success in the digital age.',
        icon: 'fas fa-bullseye',
        color: '#dc3545'
      },
      vision: {
        title: 'Our Vision',
        description: 'To be the leading technology partner that transforms ideas into reality, creating a more connected and efficient world through innovative solutions.',
        icon: 'fas fa-eye',
        color: '#28a745'
      },
      values: [
        {
          title: 'Excellence',
          description: 'We strive for excellence in everything we do, delivering solutions that exceed expectations.',
          icon: 'fas fa-star'
        },
        {
          title: 'Integrity',
          description: 'We maintain the highest standards of honesty, transparency, and ethical conduct.',
          icon: 'fas fa-handshake'
        },
        {
          title: 'Innovation',
          description: 'We continuously explore new technologies and methodologies to stay ahead of the curve.',
          icon: 'fas fa-lightbulb'
        },
        {
          title: 'Collaboration',
          description: 'We believe in the power of teamwork and collaboration to achieve extraordinary results.',
          icon: 'fas fa-users'
        }
      ]
    })

    const teamContent = ref({
      sectionLabel: 'Our Team',
      mainTitle: 'Meet the Experts',
      description: 'Our diverse team of professionals brings together years of experience and expertise across various domains.',
      teamMembers: [
        {
          name: 'John Smith',
          position: 'CEO & Founder',
          image: '/resource/author-01.png',
          bio: 'Visionary leader with 15+ years in technology and business development.',
          social: {
            linkedin: '#',
            twitter: '#',
            email: 'john@sogdo.com'
          }
        },
        {
          name: 'Sarah Johnson',
          position: 'CTO',
          image: '/resource/author-02.png',
          bio: 'Technical expert specializing in AI, machine learning, and cloud architecture.',
          social: {
            linkedin: '#',
            github: '#',
            email: 'sarah@sogdo.com'
          }
        },
        {
          name: 'Mike Chen',
          position: 'Lead Developer',
          image: '/resource/author-03.png',
          bio: 'Full-stack developer with expertise in modern web technologies and mobile development.',
          social: {
            linkedin: '#',
            github: '#',
            email: 'mike@sogdo.com'
          }
        },
        {
          name: 'Emily Davis',
          position: 'UX/UI Designer',
          image: '/resource/author-04.png',
          bio: 'Creative designer focused on user experience and interface design.',
          social: {
            linkedin: '#',
            dribbble: '#',
            email: 'emily@sogdo.com'
          }
        }
      ]
    })

    const journeyContent = ref({
      sectionLabel: 'Our Journey',
      mainTitle: 'Company Timeline',
      description: 'From humble beginnings to becoming a trusted technology partner, our journey has been marked by continuous growth and innovation.',
      timeline: [
        {
          year: '2014',
          title: 'Company Founded',
          description: 'Started as a small team with a big vision to revolutionize technology solutions.',
          icon: 'fas fa-rocket'
        },
        {
          year: '2016',
          title: 'First Major Project',
          description: 'Successfully delivered our first enterprise-level solution, establishing our reputation.',
          icon: 'fas fa-trophy'
        },
        {
          year: '2018',
          title: 'Team Expansion',
          description: 'Grew our team to 20+ professionals and expanded our service offerings.',
          icon: 'fas fa-users'
        },
        {
          year: '2020',
          title: 'Digital Transformation',
          description: 'Pivoted to remote work and enhanced our digital capabilities during the pandemic.',
          icon: 'fas fa-laptop'
        },
        {
          year: '2022',
          title: 'AI Integration',
          description: 'Launched our AI and machine learning services, opening new possibilities.',
          icon: 'fas fa-brain'
        },
        {
          year: '2024',
          title: 'Global Expansion',
          description: 'Expanded our services globally and established partnerships worldwide.',
          icon: 'fas fa-globe'
        }
      ]
    })

    const clientsContent = ref({
      sectionLabel: 'Trusted Partners',
      mainTitle: 'Our Clients & Partners',
      description: 'We are proud to work with leading companies and organizations across various industries.',
      clients: [
        { name: 'TechCorp', logo: '/resource/01.png', industry: 'Technology' },
        { name: 'FinancePro', logo: '/resource/02.png', industry: 'Finance' },
        { name: 'HealthCare Plus', logo: '/resource/choose-01.png', industry: 'Healthcare' },
        { name: 'EduTech Solutions', logo: '/resource/choose-02.svg.svg', industry: 'Education' },
        { name: 'RetailMax', logo: '/resource/choose-03.png', industry: 'Retail' },
        { name: 'Manufacturing Co', logo: '/resource/choose-04.png', industry: 'Manufacturing' }
      ],
      testimonials: [
        {
          quote: 'Sogdo transformed our digital infrastructure and helped us achieve 300% growth in online sales.',
          author: 'David Wilson',
          position: 'CEO, TechCorp',
          image: '/resource/author-01.png'
        },
        {
          quote: 'Their AI solutions revolutionized our customer service and improved satisfaction by 95%.',
          author: 'Lisa Brown',
          position: 'CTO, FinancePro',
          image: '/resource/author-02.png'
        }
      ]
    })

    const ctaContent = ref({
      backgroundImage: '/resource/portfolio-bg.jpg',
      sectionLabel: 'Ready to Start?',
      mainTitle: 'Let\'s Build Something Amazing Together',
      description: 'Ready to transform your ideas into reality? Get in touch with our team and let\'s discuss how we can help you achieve your goals.',
      buttonText: 'Get Started Today',
      buttonLink: '/home-contact',
      features: [
        {
          icon: 'fas fa-clock',
          title: 'Quick Response',
          description: 'We respond to all inquiries within 24 hours'
        },
        {
          icon: 'fas fa-shield-alt',
          title: 'Secure & Reliable',
          description: 'Your data and projects are protected with enterprise-grade security'
        },
        {
          icon: 'fas fa-headset',
          title: '24/7 Support',
          description: 'Round-the-clock support for all your technical needs'
        }
      ]
    })

    onMounted(() => {
      new WOW({
        mobile: false,
        live: false
      }).init()
    })

    return {
      introContent,
      missionVisionContent,
      teamContent,
      journeyContent,
      clientsContent,
      ctaContent
    }
  }
}
</script>

<style scoped>
/* Home About Page Styles */
.home-about-page {
  position: relative;
}

.about-banner-bg {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background-image: url('~@/assets/images/banner/banner1.png');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  z-index: -1;
  opacity: 0.1;
}

/* Add padding for banner sections */
.home-about-page section.banner__two {
  padding-top: 100px !important;
  padding-bottom: 100px !important;
}

@media (max-width: 768px) {
  .home-about-page section.banner__two {
    padding-top: 80px !important;
    padding-bottom: 80px !important;
  }
}

@media (max-width: 480px) {
  .home-about-page section.banner__two {
    padding-top: 60px !important;
    padding-bottom: 60px !important;
  }
}
</style>
